import React from 'react';
import StaffProfileTemplate, {
  ProfileSection,
  ProfileParagraphs,
  ProfileBullets,
  ProfilePublications,
  ProfileLinkList,
  ProfileContactSection,
} from './StaffProfileTemplate';

const SaktumWontiProfile = () => {
  const person = {
    id: 'saktum-wonti',
    name: 'Saktum Wonti',
    honorific: 'Ms',
    title: 'Assistant Researcher',
    affiliation: 'The Highland Institute, Kohima, Nagaland',
    image: 'https://customer-assets.emergentagent.com/job_roadwise-asia/artifacts/5845y2ey_Saktum.jpeg',
    email: 'saktum@highlandinstitute.org',
    category: 'Research Staff',
    testid: 'saktum-wonti-profile',
  };

  return (
    <StaffProfileTemplate person={person}>
      <ProfileSection title="Intellectual Biography">
        <ProfileParagraphs
          items={[
            'Saktum Wonti is an Assistant Researcher at The Highland Institute, where her work focuses on Indigenous knowledge systems, oral histories, climate change, conflict, and material culture in Northeast India and the Naga–Myanmar borderlands. Grounded in ethnographic and community-based research methodologies, her work explores the relationships between memory, landscape, ecological knowledge, and historical experience, with particular attention to how borderland communities preserve and reinterpret histories through oral narratives, material objects, and everyday practices.',
            'Her research has engaged with themes of Traditional Ecological Knowledge, environmental change, food systems, healing traditions, and wartime memory. She was a team member on the IDRC Canada-funded Earthkeepers project on Traditional Ecological Knowledge and Climate Resilience in Eastern Nagaland, which examined local understandings of environmental transformation, sustainability, and ecological resilience through community knowledge and lived experience. As part of this work, she participated in field-based documentation, community engagement, and public seminars in villages, contributing to collaborative discussions on climate change, Indigenous knowledge, and environmental adaptation.',
            'Saktum is currently involved in the project "War Residues: Repurposed Objects and Memories of a Global War along the Highland Naga–Myanmar Border", supported by the Delta on The Move Foundation. The initiative documents memories and material remains of the Second World War across the Highland Naga–Myanmar borderlands, examining how repurposed wartime objects, aircraft fragments, and military remnants have been transformed into everyday tools and domestic items. Through material culture, oral memory, and landscape narratives, the project explores how communities preserve and reinterpret histories of conflict.',
            'Her broader intellectual interests include borderland histories, Indigenous ecological knowledge, environmental humanities, oral history, and the cultural life of material objects. She is particularly interested in the ways memory and historical imagination are embedded within landscapes, objects, and community practices.',
          ]}
        />
      </ProfileSection>

      <ProfileSection title="Collaborative and Public-Facing Projects">
        <ProfileParagraphs
          items={[
            <>
              In addition to research, Saktum has contributed to collaborative cultural initiatives at The Highland Institute. She contributed to the documentary <em>Healing Hands: Tale of an Angami Healer</em>, developed in collaboration with the School of Film, Media and Creative Arts, Rashtreeya Vidyalaya University. The documentary, which explored Indigenous healing traditions among the Angami community, focusing on the relationship between herbal knowledge, spirituality, faith, and the natural world, was screened at the Berlin International Film Festival in July 2025.
            </>,
            <>
              She has also contributed to exhibitions and other public-facing initiatives, including <em>Look Up</em> and <em>Ancestral Voices</em>, which explored Indigenous knowledge, ecological understanding, memory, and cultural heritage through public engagement and visual storytelling. In addition, she has been involved with the Highland Institute Film Club, Environment Day programmes, and the Tasting Tomorrow project, which explores Indigenous food systems, climate change, and future food resilience. Her work across these projects reflects a broader interest in public anthropology, community engagement, and participatory approaches to cultural documentation.
            </>,
            'In addition, Saktum worked as a Research Assistant for a University of Göttingen study examining changing food preparation and consumption patterns in Nagaland at the household, market, and community levels. The project explored transformations in food systems, dietary practices, and socio-economic change within Naga communities.',
            <>
              She has also participated in several international academic and policy forums, including the Global Development Conference 2024 in Fiji, a panel discussion in Chiang Mai for the launch of the book <em>Conflict, Complexity and Climate Change</em>, and the International Public Policy Association (IPPA) Winter School in 2023, engaging with themes of climate resilience, sustainable development, public policy, governance, and conflict in Myanmar.
            </>,
          ]}
        />
      </ProfileSection>

      <ProfileSection title="Research Foci & Areas of Work">
        <ProfileBullets
          items={[
            'Indigenous knowledge systems and Traditional Ecological Knowledge (TEK)',
            'Climate change and environmental histories',
            'Oral history and memory studies',
            'Conflict, borderlands, and wartime histories',
            'Material culture and repurposed war relics',
            'Ethnographic and participatory fieldwork',
            'Community-based cultural documentation',
            'Landscape, place, and historical imagination',
            'Indigenous healing traditions and ecological belief systems',
            'Food systems, culinary heritage, and environmental change',
            'Naga–Myanmar borderland studies',
          ]}
        />
      </ProfileSection>

      <ProfileSection title="Publications, Media & Public Scholarship">
        <ProfilePublications
          items={[
            {
              year: '2024',
              citation: (
                <>
                  'Tasting Tomorrow: Exploring climate-adapted cuisines in Nagaland' in <em>The Highlander Journal</em>, Vol. 3, Issue 2, pp. 73–75.
                </>
              ),
              url: 'https://doi.org/10.5281/zenodo.11001758',
            },
            {
              year: '2024',
              citation: (
                <>
                  'Earthkeepers Project: Noklak Border Mission' in <em>Highlander</em>, Vol. 3, Issue 2, pp. 116–124.
                </>
              ),
              url: 'https://doi.org/10.5281/zenodo.11002249',
            },
            {
              year: '2025',
              citation: (
                <>
                  'Traditional ecological knowledge and climate resilience in Eastern Nagaland: Insights from the Earthkeepers Project 2023–2025' unpublished report, Earthkeepers Project, The Highland Institute.
                </>
              ),
            },
          ]}
        />
        <h3 className="font-semibold text-stone-900 mt-8 mb-3 not-prose">Selected Blogs</h3>
        <ul className="space-y-2 not-prose">
          <li className="flex items-start gap-3 text-stone-700"><span className="text-green-600 mt-1">•</span><span>Seed Guardians of the Hills</span></li>
          <li className="flex items-start gap-3 text-stone-700"><span className="text-green-600 mt-1">•</span><span>Sleep in India; Reap in Myanmar: how villagers defy division on a porous border</span></li>
          <li className="flex items-start gap-3 text-stone-700"><span className="text-green-600 mt-1">•</span><span>The Call of Home: Rediscovering my Roots on the Indo-Myanmar Border</span></li>
        </ul>
      </ProfileSection>

      <ProfileSection title="Postgraduate Qualifications">
        <ProfileBullets items={['MSc in Anthropology']} />
      </ProfileSection>

      <ProfileSection title="Awards & Fellowships">
        <ProfileBullets items={['National Eligibility Test (NET)']} />
      </ProfileSection>

      <ProfileContactSection
        email={person.email}
        role="Assistant Researcher, The Highland Institute"
        testidPrefix="saktum"
        scholarlyProfiles={[
          { label: 'ORCID', url: 'https://orcid.org/0009-0005-4993-6058', testid: 'saktum-orcid-link' },
          { label: 'LinkedIn', url: 'https://www.linkedin.com/in/saktum-wonti-60ab59239', testid: 'saktum-linkedin-link' },
        ]}
      />
    </StaffProfileTemplate>
  );
};

export default SaktumWontiProfile;
